/**
 * 발주서 품목 검색·선택 — 재고관리 '품명&품목'(item_master) 기반.
 *
 * 동작:
 *   - input 은 자유 텍스트 (품목명). focus/typing 시 드롭다운 열림
 *   - 검색은 품목그룹 + 품목명 + 규격 통합 (대소문자 무시 부분일치)
 *   - 드롭다운 맨 위: "✍ 목록에 없음 — 직접 작성" (현재 입력값 그대로 유지하고 닫기)
 *   - 그 아래: 후보 항목 (품목명 + 그룹 배지 + 규격). 클릭 → onPick(item) + 닫기
 *   - 매입처(supplier)가 지정돼 있으면 부모가 그 매입처 매입 품목만 candidates 로 내려줌
 *
 * 후보 목록(candidates)은 부모(발주서 폼)에서 매입처 기준으로 1회 로드해 내려준다.
 * 품목 선택 시 품목명·규격(상세) 채움은 호출자(onPick)가 담당.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { PoLookupItem } from '../lib/po-api';

interface Props {
  value: string;
  candidates: PoLookupItem[];
  /** true = candidates 가 특정 매입처의 매입 품목으로 한정된 상태 */
  supplierFiltered: boolean;
  /** 매입처 표시명 (헤더 안내용) */
  supplierLabel: string;
  loading?: boolean;
  onChange: (v: string) => void;
  onPick: (item: PoLookupItem) => void;
}

export function PoItemSearchSelect({
  value,
  candidates,
  supplierFiltered,
  supplierLabel,
  loading,
  onChange,
  onPick,
}: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const matches = useMemo(() => {
    const q = value.trim().toLowerCase();
    const list = q
      ? candidates.filter((c) =>
          [c.itemGroup, c.itemName, c.spec ?? ''].join(' ').toLowerCase().includes(q),
        )
      : candidates;
    return list.slice(0, 50);
  }, [value, candidates]);

  return (
    <div ref={ref} className="relative">
      <input
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          if (!open) setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="품목 검색 (그룹·품명·규격)"
        className="ui-input ui-input-sm w-full"
      />
      {open && (
        <div className="absolute z-30 left-0 top-full mt-0.5 min-w-full w-[24rem] max-w-[80vw] max-h-80 overflow-y-auto bg-white border border-slate-300 rounded shadow-lg">
          {/* 맨 위 — 목록에 없음, 직접 작성 (현재 입력값 유지) */}
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="w-full text-left px-2 py-1.5 text-xs bg-blue-50 hover:bg-blue-100 border-b border-slate-200 font-medium text-blue-800"
          >
            ✍ 목록에 없음 — 직접 작성{value.trim() ? ` : "${value.trim()}"` : ''}
          </button>

          {/* 컨텍스트 헤더 */}
          <div className="px-2 py-1 text-[11px] text-slate-500 bg-slate-50 border-b border-slate-200 sticky top-0">
            {loading
              ? '품목 불러오는 중…'
              : supplierFiltered
                ? `‘${supplierLabel}’ 매입 품목 ${candidates.length}건 · 검색결과 ${matches.length}건`
                : `전체 품명&품목 ${candidates.length}건 · 검색결과 ${matches.length}건`}
          </div>

          {matches.length > 0 ? (
            matches.map((it) => (
              <button
                key={it.itemCode}
                type="button"
                onClick={() => {
                  onPick(it);
                  setOpen(false);
                }}
                className="w-full text-left px-2 py-1.5 text-xs hover:bg-emerald-50 border-b border-slate-100 last:border-b-0"
              >
                <div className="flex items-center gap-1.5">
                  <span className="font-medium text-slate-800">{it.itemName}</span>
                  {it.itemGroup && (
                    <span className="shrink-0 text-[11px] px-1 py-0.5 rounded bg-slate-100 text-slate-500">
                      {it.itemGroup}
                    </span>
                  )}
                </div>
                {it.spec && (
                  <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-2 whitespace-pre-wrap">
                    {it.spec}
                  </div>
                )}
              </button>
            ))
          ) : (
            <div className="px-2 py-3 text-center text-xs text-slate-500">
              {supplierFiltered
                ? '이 매입처로 등록된 매입 품목이 없습니다 — 위 [직접 작성] 하거나 매입처를 비우면 전체에서 검색됩니다'
                : '일치하는 품목 없음 — 위 [직접 작성] 으로 추가'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
